"use client";

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

interface TypingAnimationProps {
  texts: string[];
  typingDuration?: number;
  pauseDuration?: number;
  erasingDuration?: number;
  className?: string;
}

export function TypingAnimation({
  texts,
  typingDuration = 100,
  pauseDuration = 2000,
  erasingDuration = 50,
  className,
}: TypingAnimationProps) {
  const [displayedText, setDisplayedText] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'erasing'>('typing');
  const [showCursor, setShowCursor] = useState<boolean>(true);

  useEffect(() => {
    const currentText = texts[currentIndex];
    let timeoutId: ReturnType<typeof setTimeout>;

    console.log('Animation state:', { phase, currentIndex, displayedText: displayedText.length, currentText });

    switch (phase) {
      case 'typing':
        if (displayedText.length < currentText.length) {
          timeoutId = setTimeout(() => {
            setDisplayedText(currentText.substring(0, displayedText.length + 1));
          }, typingDuration);
        } else {
          // Finished typing, start pause
          timeoutId = setTimeout(() => {
            setPhase('pausing');
          }, 100);
        }
        break;

      case 'pausing':
        // Pause phase - wait then start erasing
        timeoutId = setTimeout(() => {
          setPhase('erasing');
        }, pauseDuration);
        break;

      case 'erasing':
        if (displayedText.length > 0) {
          timeoutId = setTimeout(() => {
            setDisplayedText(displayedText.substring(0, displayedText.length - 1));
          }, erasingDuration);
        } else {
          // Finished erasing, move to next text
          setCurrentIndex((prev) => (prev + 1) % texts.length);
          setPhase('typing');
        }
        break;
    }

    return () => clearTimeout(timeoutId);
  }, [displayedText, currentIndex, phase, texts, typingDuration, pauseDuration, erasingDuration]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <h1
      className={cn(
        "font-display text-center text-4xl font-bold leading-[5rem] tracking-[-0.02em] drop-shadow-sm",
        className,
      )}
    >
      {displayedText}
      <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}>|</span>
    </h1>
  );
}